import { useRef } from 'react';
import { ChevronDown, FileText, X } from 'lucide-react';

const ACCEPT = '.pdf,.docx,.txt,.json,.csv';

// Collapsible optional product-specs dropzone (DC §4.5).
export function SpecsDropzone({
  open,
  onToggle,
  uploadedFile,
  onUpload,
  onRemove,
}: {
  open: boolean;
  onToggle: () => void;
  uploadedFile: File | null;
  onUpload: (file: File) => void;
  onRemove: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div style={{ marginTop: 20, paddingTop: 18, borderTop: '1px solid var(--border)' }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'none',
          border: 'none',
          padding: 0,
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
          Have product specs? <span style={{ color: 'var(--silver-mist)' }}>(optional)</span>
        </span>
        <span
          style={{
            display: 'inline-flex',
            transition: 'transform var(--duration-base) var(--ease-standard)',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <ChevronDown style={{ width: 15, height: 15, color: 'var(--muted-foreground)' }} />
        </span>
      </button>

      {open && (
        <div style={{ marginTop: 13 }}>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.currentTarget.value = '';
            }}
          />

          {uploadedFile ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '12px 14px',
                background: 'var(--card)',
              }}
            >
              <FileText style={{ width: 16, height: 16, color: 'var(--signal-blue)', flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--foreground)' }}>
                {uploadedFile.name}
              </span>
              <span role="button" onClick={onRemove} style={{ display: 'inline-flex', cursor: 'pointer' }}>
                <X style={{ width: 15, height: 15, color: 'var(--muted-foreground)' }} />
              </span>
            </div>
          ) : (
            <div
              onClick={() => inputRef.current?.click()}
              style={{
                border: '1px dashed var(--silver-mist)',
                borderRadius: 10,
                padding: 22,
                textAlign: 'center',
                background: 'var(--background)',
                cursor: 'pointer',
              }}
            >
              <p style={{ fontSize: 13, color: 'var(--muted-foreground)', margin: '0 0 4px' }}>
                Drop a product spec sheet, or <span style={{ color: 'var(--signal-blue)', fontWeight: 600 }}>browse</span>
              </p>
              <p style={{ fontSize: 12, color: 'var(--silver-mist)', margin: 0 }}>
                PDF, DOCX, or TXT. Improves grounding accuracy
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
